import { Alert, Button, Code, CopyButton, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { notifyError } from '../../lib/notify';

/**
 * Setting a register PIN: one click, and the server hands back six digits to read out.
 *
 * This used to be two password fields — type a temporary PIN, type it again — which put a manager
 * in the business of INVENTING a PIN, and people invent the way people invent: birthdays, 1234, the
 * same one for everybody. A PIN somebody typed is a PIN somebody knows. The server generates now;
 * the digits exist on this screen once, and nothing but hashes survives anywhere.
 *
 * IT MAY NOW SAY THE HOLDER WILL BE MADE TO CHOOSE THEIR OWN, and it says it once. It said it twice
 * for a long time while nothing did it — the server set `mustChangePin`, the register showed nothing,
 * and the person worked every shift after on digits their manager read off this screen. Both
 * sentences were deleted rather than softened, because a promise about a screen nobody has written
 * belongs in the ticket for that screen. The screen exists now: the register refuses every trading
 * route on a PIN set here until it has been replaced.
 *
 * THE SENTENCE IS STILL ABOUT WHAT THE PERSON WILL SEE and not about a flag. "They will be asked to
 * choose their own before they can start work" is checkable by the manager standing there — she hands
 * the tablet over and watches it happen. Anything about must-change flags or policy is an internal
 * artifact and belongs nowhere near this modal.
 *
 * The component still owns its own state for the reason the previous version documented: Mantine's
 * modal manager stores the element it is handed at open time and renders it from the app root, so
 * state owned by the opening button is state captured before anything happened.
 */
export function GeneratedPinDisplay({ pin, forWhom }: { pin: string; forWhom: string }) {
  return (
    <Stack gap="md">
      <Alert color="yellow" variant="light" title="Shown once">
        This is the only time this PIN is visible — it is not stored and cannot be looked up. Read it out to{' '}
        {forWhom} now. The register will ask {forWhom} to choose their own before they can start work.
      </Alert>
      <Group justify="center" gap="md">
        <Code fz={34} px="lg" py="xs" fw={700} style={{ letterSpacing: '0.35em' }}>
          {pin}
        </Code>
        <CopyButton value={pin}>
          {({ copied, copy }) => (
            <Button variant="default" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          )}
        </CopyButton>
      </Group>
      <Group justify="flex-end">
        <Button onClick={() => modals.closeAll()}>Done</Button>
      </Group>
    </Stack>
  );
}

function PinResetForm({
  staffId,
  staffName,
  onDone,
}: {
  staffId: string;
  staffName: string;
  /**
   * Told to the page so it can re-read the person, rather than reaching into the cache from in
   * here — the page that owns the query is the one that decides when it is stale.
   */
  onDone?: () => void;
}) {
  const [pin, setPin] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: () =>
      rawApi<{ ok: true; pin?: string }>('POST', `/admin/v1/staff/members/${staffId}/pin`, {}),
    onSuccess: (res) => {
      onDone?.();
      setPin(res.pin ?? null);
    },
    onError: (err) => notifyError(err, 'set this PIN'),
  });

  if (pin) {
    return <GeneratedPinDisplay pin={pin} forWhom={staffName} />;
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        A PIN is generated for you — nobody types one, so nobody invents one. It appears once on this screen
        and is never shown again; read it out to {staffName} then. It is temporary: the register asks{' '}
        {staffName} to choose their own before it will let them do anything, and it ends any session they
        already have open.
      </Text>
      <Group justify="flex-end" mt="sm">
        <Button variant="default" onClick={() => modals.closeAll()} disabled={generate.isPending}>
          Cancel
        </Button>
        <Button onClick={() => generate.mutate()} loading={generate.isPending}>
          Generate a temporary PIN
        </Button>
      </Group>
    </Stack>
  );
}

export function openPinResetModal(staffId: string, staffName: string, onDone?: () => void) {
  modals.open({
    title: `Set a register PIN for ${staffName}`,
    children: <PinResetForm staffId={staffId} staffName={staffName} onDone={onDone} />,
  });
}
